import { useMemo } from 'react';
import { useAllMessages } from './useAllMessages';
import { useAliases } from '../../aliases/hooks/useAliases';
import { useTags } from '../../tags/hooks/useTags';
import { MessageStatus } from '../../messages/types';
import { AnalyticsData, EmailsByDate, AliasActivity, TagUsage } from '../types';
import dayjs from 'dayjs';

export const useAnalytics = (days: number = 30) => {
    const { items: messages, loading: messagesLoading } = useAllMessages();
    const { items: aliases } = useAliases();
    const { items: tags } = useTags();

    const analyticsData: AnalyticsData = useMemo(() => {
        const startDate = dayjs().subtract(days, 'day').startOf('day');
        const filteredMessages = messages.filter(msg => 
            msg.date && dayjs(msg.date).isAfter(startDate) && msg.status !== MessageStatus.ARCHIVED
        );

        // Emails by date
        const dateMap = new Map<string, { sent: number; received: number }>();
        for (let i = 0; i < days; i++) {
            const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
            dateMap.set(date, { sent: 0, received: 0 });
        }

        filteredMessages.forEach(msg => {
            const date = dayjs(msg.date).format('YYYY-MM-DD');
            const current = dateMap.get(date) || { sent: 0, received: 0 };
            if (msg.status === MessageStatus.SENT) {
                current.sent++;
            } else {
                current.received++;
            }
            dateMap.set(date, current);
        });

        const emailsByDate: EmailsByDate[] = Array.from(dateMap.entries())
            .map(([date, counts]) => ({ date, ...counts }))
            .sort((a, b) => a.date.localeCompare(b.date));

        // Alias activity
        const aliasMap = new Map<string, number>();
        filteredMessages.forEach(msg => {
            if (msg.aliasId) {
                aliasMap.set(msg.aliasId, (aliasMap.get(msg.aliasId) || 0) + 1);
            }
        });

        const aliasActivity: AliasActivity[] = Array.from(aliasMap.entries())
            .map(([aliasId, count]) => {
                const alias = aliases.find(a => a.id === aliasId);
                return {
                    aliasId,
                    aliasName: alias?.name || aliasId.substring(0, 8),
                    count
                };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        // Tag usage
        const tagMap = new Map<string, number>();
        filteredMessages.forEach(msg => {
            msg.tagIds?.forEach(tagId => {
                tagMap.set(tagId, (tagMap.get(tagId) || 0) + 1);
            });
        });

        const tagUsage: TagUsage[] = Array.from(tagMap.entries())
            .map(([tagId, count]) => {
                const tag = tags.find(t => t.id === tagId);
                return {
                    tagId,
                    tagName: tag?.name || tagId.substring(0, 8),
                    count
                };
            })
            .sort((a, b) => b.count - a.count);

        // Totals
        const totalSent = filteredMessages.filter(m => m.status === MessageStatus.SENT).length;
        const totalReceived = filteredMessages.filter(m => m.status !== MessageStatus.SENT).length;

        return {
            emailsByDate,
            aliasActivity,
            tagUsage,
            totalSent,
            totalReceived,
            totalMessages: filteredMessages.length
        };
    }, [messages, aliases, tags, days]);

    return {
        data: analyticsData,
        loading: messagesLoading
    };
};
